"use server";

import { auth } from "@/auth";
import type { ActionResult } from "@/types/server-action";
import { validateParams } from "@/lib/utils";
import { z } from "zod";
import type { Draft } from "@/prisma/generated/client";
import { resolveSaleTypeTags } from "@/lib/cat/company-category-badge";
import { updateDraftAfterStepSave } from "@/lib/cat/draft-step-save";
import { prisma } from "@/prisma";
import {
  buildDraftLockHolder,
  DraftLockDeniedError,
} from "@/lib/cat/draft-lock";

const saveDraftSaleTypeStepSchema = z.object({
  id: z.number().int().positive(),
  saleType: z.enum([
    "PART",
    "RENT",
    "PSTD",
    "LCDD",
    "PSTE-X",
    "PSTE-L",
    "PSTE-R",
    "MFL",
    "CHCA",
    "SFL",
    "MONO",
  ]),
});

export async function saveDraftSaleTypeStep(
  params: z.infer<typeof saveDraftSaleTypeStepSchema>,
): Promise<ActionResult<Draft>> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return { success: false, message: "Non authentifié." };
    }

    const { id, saleType } = validateParams(
      saveDraftSaleTypeStepSchema,
      params,
    );

    const sessionKvps = session.data?.kvps?.current?.identifier ?? String(id);
    const lockHolder = buildDraftLockHolder(session.user);

    const draft = await prisma.draft.findUnique({ where: { id } });
    if (!draft) {
      return { success: false, message: "Brouillon introuvable." };
    }

    const savedDraft = await updateDraftAfterStepSave({
      draftId: id,
      stepId: "selling_type_retail",
      userId: lockHolder.userId,
      userName: lockHolder.userName,
      kvps: sessionKvps,
      data: {
        saleType,
        saleTypeTags: resolveSaleTypeTags(draft) ?? [],
      },
    });

    return { success: true, data: savedDraft };
  } catch (error) {
    console.error("[saveDraftSaleTypeStep]", error);

    if (error instanceof DraftLockDeniedError) {
      return { success: false, message: error.message };
    }

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}
